import React, { useMemo, useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ChevronRight, ChevronDown, ArrowUp, ArrowDown, Plus, Trash2, X, AlertTriangle,
} from "lucide-react";

// ---------------------------------------------------------------------------
// MP11 Phase 1 — Rights Party editor.
//
// A rights party (writer / composer / publisher / administrator /
// rightsholder) is now a structured entry with its own repeatable list of
// PROs and IPI/CAE identifiers. This module renders three levels of edit
// primitives, each with add / edit / remove / reorder:
//
//   RightsPartySection   — one section (Writers, Publishers, etc.)
//     └── RightsPartyEditor  — one party card (collapsed by default)
//           ├── PROEntryRow  — one PRO affiliation for that party
//           └── IPIEntryRow  — one IPI / CAE number for that party
//
// Design principles
// -----------------
// * Compact by default. Each party card shows only [Name · N PROs · M IPIs]
//   until the operator expands it. Prevents visual clutter on tracks with
//   many contributors.
// * "Add another…" controls rather than pre-provisioned empty rows.
// * Individual remove buttons on every repeatable entry (party, PRO, IPI).
// * Reorder via up/down arrow buttons (keyboard-accessible, mobile-safe).
// * Soft, non-blocking IPI/CAE format validation (warns; never blocks save).
// * PRO name uses a datalist backed by the PRO / CMO directory when
//   available, but ALWAYS falls through to free text (international PROs
//   may not be in the local directory).
// ---------------------------------------------------------------------------

const PARTY_TYPE_LABELS = {
  writer:        "Writer",
  composer:      "Composer",
  publisher:     "Publisher",
  administrator: "Administrator",
  rightsholder:  "Rightsholder",
};

const CAPACITY_OPTIONS = [
  "Writer",
  "Composer",
  "Publisher",
  "Administrator",
  "Rightsholder",
  "Performer",
  "Producer",
  "Other",
];

function newUuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  // Fallback (RFC4122 v4-ish) — sufficient for client-side entry ids.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Soft validation: IPI/CAE numbers are typically 9–11 digits. We warn (not
// block) on anything else so legitimate variations from other territories
// aren't rejected.
function isLikelyValidIPI(raw) {
  if (!raw) return true; // empty is fine — required-ness is enforced elsewhere
  const digits = String(raw).replace(/\D/g, "");
  return digits.length >= 9 && digits.length <= 11;
}

// Move an item within an array (immutable).
function moveItem(arr, index, delta) {
  const target = index + delta;
  if (target < 0 || target >= arr.length) return arr;
  const copy = arr.slice();
  const [item] = copy.splice(index, 1);
  copy.splice(target, 0, item);
  return copy;
}

// ---------- PRO directory hook (shared across all rows) --------------------

let _proCache = null;
export function usePRODirectory() {
  const [societies, setSocieties] = useState(_proCache || []);
  const [loading, setLoading] = useState(!_proCache);
  useEffect(() => {
    if (_proCache) return;
    let alive = true;
    api.get("/pro-directory")
      .then((r) => {
        if (!alive) return;
        _proCache = Array.isArray(r.data) ? r.data.filter((s) => s.active_status !== false) : [];
        setSocieties(_proCache);
      })
      .catch(() => { if (alive) setSocieties([]); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);
  const proNames = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const s of societies || []) {
      const label = s.society_name || s.society_code || "";
      if (label && !seen.has(label.toLowerCase())) {
        seen.add(label.toLowerCase());
        out.push({ code: s.society_code || "", name: label, territory: s.country_or_territory || "" });
      }
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [societies]);
  return { proNames, loading };
}

// ---------- Section (list of parties) --------------------------------------

export function RightsPartySection({
  label,
  partyType,
  parties,
  onChange,
  helperText,
  emptyHint,
  addLabel,
  testid,
}) {
  const list = Array.isArray(parties) ? parties : [];

  function updateAt(idx, next) {
    const copy = list.slice();
    copy[idx] = next;
    onChange(copy);
  }
  function removeAt(idx) {
    const next = list.filter((_, i) => i !== idx);
    onChange(next);
  }
  function moveAt(idx, delta) {
    onChange(moveItem(list, idx, delta));
  }
  function addNew() {
    onChange([
      ...list,
      {
        id: newUuid(),
        party_type: partyType,
        name: "",
        legal_name: "",
        pros: [],
        ipis: [],
        notes: "",
        _initiallyOpen: true, // hint for editor to expand fresh entries
      },
    ]);
  }

  return (
    <div className="md:col-span-2" data-testid={testid || `rights-party-section-${partyType}`}>
      <div className="flex items-baseline justify-between gap-2">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">
          {label}
        </Label>
        <span className="text-[11px] text-muted-foreground" data-testid={`${testid || `rights-party-${partyType}`}-count`}>
          {list.length} {list.length === 1 ? "entry" : "entries"}
        </span>
      </div>
      {helperText && (
        <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
          {helperText}
        </p>
      )}
      <div className="mt-2 space-y-2">
        {list.length === 0 && (
          <div
            className="rounded-md border border-dashed text-[12px] text-muted-foreground px-3 py-2.5"
            data-testid={`${testid || `rights-party-${partyType}`}-empty`}
          >
            {emptyHint || `No ${label.toLowerCase()} added yet.`}
          </div>
        )}
        {list.map((p, idx) => (
          <RightsPartyEditor
            key={p.id || `p-${idx}`}
            party={p}
            partyType={partyType}
            onChange={(next) => updateAt(idx, next)}
            onRemove={() => removeAt(idx)}
            onMoveUp={() => moveAt(idx, -1)}
            onMoveDown={() => moveAt(idx, +1)}
            canMoveUp={idx > 0}
            canMoveDown={idx < list.length - 1}
            testid={`${testid || `rights-party-${partyType}`}-${idx}`}
          />
        ))}
      </div>
      <div className="mt-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addNew}
          data-testid={`${testid || `rights-party-${partyType}`}-add`}
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" />
          {addLabel || `Add Another ${PARTY_TYPE_LABELS[partyType] || "Party"}`}
        </Button>
      </div>
    </div>
  );
}

// ---------- One party (collapsible card) -----------------------------------

export function RightsPartyEditor({
  party,
  partyType,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  canMoveUp,
  canMoveDown,
  testid,
}) {
  const [open, setOpen] = useState(!!party?._initiallyOpen);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  // Strip the transient _initiallyOpen hint before saving upstream.
  function patch(fields) {
    const next = { ...party, ...fields };
    delete next._initiallyOpen;
    onChange(next);
  }

  const proCount = (party.pros || []).length;
  const ipiCount = (party.ipis || []).length;
  const typeLabel = PARTY_TYPE_LABELS[partyType] || "Party";
  const summary = (party.name || "").trim() || `Unnamed ${typeLabel.toLowerCase()}`;

  return (
    <Card
      className="border-[hsl(347_60%_22%)]/15 bg-secondary/20"
      data-testid={testid}
    >
      <CardContent className="p-3">
        {/* ---------- Header row ---------- */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="flex items-center gap-1 text-sm text-left flex-1 min-w-0"
            onClick={() => setOpen((v) => !v)}
            data-testid={`${testid}-toggle`}
            aria-expanded={open}
          >
            {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
            <span className="font-medium truncate" data-testid={`${testid}-summary`}>
              {summary}
            </span>
            <span className="ml-2 text-[11px] text-muted-foreground shrink-0">
              · {proCount} {proCount === 1 ? "PRO" : "PROs"} · {ipiCount} IPI/CAE
            </span>
          </button>
          <div className="flex items-center gap-0.5 shrink-0">
            <IconBtn
              title="Move up"
              onClick={onMoveUp}
              disabled={!canMoveUp}
              testid={`${testid}-move-up`}
            >
              <ArrowUp className="w-3.5 h-3.5" />
            </IconBtn>
            <IconBtn
              title="Move down"
              onClick={onMoveDown}
              disabled={!canMoveDown}
              testid={`${testid}-move-down`}
            >
              <ArrowDown className="w-3.5 h-3.5" />
            </IconBtn>
          </div>
        </div>

        {/* ---------- Expanded editor ---------- */}
        {open && (
          <div className="mt-3 space-y-3">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wide">
                  {typeLabel} name
                </Label>
                <Input
                  value={party.name || ""}
                  onChange={(e) => patch({ name: e.target.value })}
                  placeholder={`${typeLabel} name…`}
                  className="mt-1"
                  data-testid={`${testid}-name`}
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wide">
                  Legal name <span className="normal-case text-muted-foreground">(optional)</span>
                </Label>
                <Input
                  value={party.legal_name || ""}
                  onChange={(e) => patch({ legal_name: e.target.value })}
                  placeholder="Legal entity name…"
                  className="mt-1"
                  data-testid={`${testid}-legal-name`}
                />
              </div>
            </div>

            {/* PROs sub-list */}
            <ProSubList
              testid={`${testid}-pros`}
              entries={party.pros || []}
              onChange={(next) => patch({ pros: next })}
            />

            {/* IPI / CAE sub-list */}
            <IpiSubList
              testid={`${testid}-ipis`}
              entries={party.ipis || []}
              onChange={(next) => patch({ ipis: next })}
            />

            <div>
              <Label className="text-[11px] uppercase tracking-wide">
                Notes <span className="normal-case text-muted-foreground">(optional)</span>
              </Label>
              <Textarea
                value={party.notes || ""}
                onChange={(e) => patch({ notes: e.target.value })}
                rows={2}
                placeholder="Chain of title, admin arrangement, etc…"
                className="mt-1 text-[12.5px]"
                data-testid={`${testid}-notes`}
              />
            </div>

            {/* Remove-this-party */}
            <div className="pt-1 flex justify-end">
              {!confirmingRemove ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-[hsl(0_70%_42%)] hover:text-[hsl(0_70%_38%)]"
                  onClick={() => setConfirmingRemove(true)}
                  data-testid={`${testid}-remove`}
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  Remove {typeLabel.toLowerCase()}
                </Button>
              ) : (
                <div
                  className="flex items-center gap-1.5 text-[11.5px]"
                  data-testid={`${testid}-remove-confirm`}
                >
                  <span className="text-muted-foreground">Remove this {typeLabel.toLowerCase()}?</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setConfirmingRemove(false)}
                    data-testid={`${testid}-remove-cancel`}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    style={{ background: "hsl(0 70% 42%)", color: "white" }}
                    onClick={() => { setConfirmingRemove(false); onRemove?.(); }}
                    data-testid={`${testid}-remove-confirm-yes`}
                  >
                    Remove
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- PRO sub-list ---------------------------------------------------

function ProSubList({ entries, onChange, testid }) {
  const { proNames } = usePRODirectory();

  function updateAt(idx, next) {
    const copy = entries.slice();
    copy[idx] = next;
    onChange(copy);
  }
  function removeAt(idx) {
    onChange(entries.filter((_, i) => i !== idx));
  }
  function moveAt(idx, delta) {
    onChange(moveItem(entries, idx, delta));
  }
  function addNew() {
    onChange([
      ...entries,
      { id: newUuid(), pro_name: "", society_code: "", territory: "", membership_ref: "", notes: "" },
    ]);
  }

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wide font-medium">
          PRO affiliations
        </div>
        <span className="text-[11px] text-muted-foreground" data-testid={`${testid}-count`}>
          {entries.length}
        </span>
      </div>
      {entries.length === 0 && (
        <div className="mt-1 rounded border border-dashed text-[12px] text-muted-foreground px-3 py-2">
          No PRO affiliations for this party.
        </div>
      )}
      <div className="mt-1 space-y-1.5">
        {entries.map((e, idx) => (
          <PROEntryRow
            key={e.id || `pro-${idx}`}
            entry={e}
            proNames={proNames}
            onChange={(n) => updateAt(idx, n)}
            onRemove={() => removeAt(idx)}
            onMoveUp={() => moveAt(idx, -1)}
            onMoveDown={() => moveAt(idx, +1)}
            canMoveUp={idx > 0}
            canMoveDown={idx < entries.length - 1}
            testid={`${testid}-${idx}`}
          />
        ))}
      </div>
      <div className="mt-1.5">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addNew}
          data-testid={`${testid}-add`}
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" />
          Add PRO
        </Button>
      </div>
    </div>
  );
}

function PROEntryRow({
  entry, proNames, onChange, onRemove, onMoveUp, onMoveDown, canMoveUp, canMoveDown, testid,
}) {
  const listId = `pro-names-${entry.id || "x"}`;
  function patch(fields) { onChange({ ...entry, ...fields }); }

  // Auto-fill society_code and territory when the user picks a directory-known
  // PRO by name (best-effort — user can still override).
  function onNameChange(val) {
    const match = (proNames || []).find(
      (p) => p.name.toLowerCase() === (val || "").toLowerCase()
    );
    if (match) {
      patch({
        pro_name: val,
        society_code: entry.society_code || match.code || "",
        territory: entry.territory || match.territory || "",
      });
    } else {
      patch({ pro_name: val });
    }
  }

  return (
    <div
      className="rounded border bg-background px-2.5 py-2"
      data-testid={testid}
    >
      <div className="grid sm:grid-cols-12 gap-2 items-start">
        <div className="sm:col-span-4">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            PRO name
          </Label>
          <Input
            value={entry.pro_name || ""}
            onChange={(e) => onNameChange(e.target.value)}
            list={listId}
            placeholder="ASCAP, BMI, SOCAN…"
            className="mt-0.5 h-8 text-[12.5px]"
            data-testid={`${testid}-name`}
          />
          <datalist id={listId}>
            {(proNames || []).map((p) => (
              <option key={p.name} value={p.name}>{p.code ? `${p.code}` : ""}</option>
            ))}
          </datalist>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Code
          </Label>
          <Input
            value={entry.society_code || ""}
            onChange={(e) => patch({ society_code: e.target.value })}
            placeholder="e.g. ASCAP"
            className="mt-0.5 h-8 font-mono text-[12.5px]"
            data-testid={`${testid}-code`}
          />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Territory
          </Label>
          <Input
            value={entry.territory || ""}
            onChange={(e) => patch({ territory: e.target.value })}
            placeholder="US, CA, UK…"
            className="mt-0.5 h-8 text-[12.5px]"
            data-testid={`${testid}-territory`}
          />
        </div>
        <div className="sm:col-span-3">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Membership #
          </Label>
          <Input
            value={entry.membership_ref || ""}
            onChange={(e) => patch({ membership_ref: e.target.value })}
            placeholder="Optional"
            className="mt-0.5 h-8 font-mono text-[12.5px]"
            data-testid={`${testid}-membership`}
          />
        </div>
        <div className="sm:col-span-1 flex items-end justify-end gap-0.5 pb-0.5">
          <IconBtn title="Move up"   onClick={onMoveUp}   disabled={!canMoveUp}   testid={`${testid}-move-up`}><ArrowUp className="w-3 h-3" /></IconBtn>
          <IconBtn title="Move down" onClick={onMoveDown} disabled={!canMoveDown} testid={`${testid}-move-down`}><ArrowDown className="w-3 h-3" /></IconBtn>
          <IconBtn title="Remove PRO entry" onClick={onRemove} testid={`${testid}-remove`}>
            <X className="w-3 h-3" />
          </IconBtn>
        </div>
      </div>
      <div className="mt-1.5">
        <Input
          value={entry.notes || ""}
          onChange={(e) => patch({ notes: e.target.value })}
          placeholder="Notes (optional)"
          className="h-8 text-[12px]"
          data-testid={`${testid}-notes`}
        />
      </div>
    </div>
  );
}

// ---------- IPI / CAE sub-list ---------------------------------------------

function IpiSubList({ entries, onChange, testid }) {
  const { proNames } = usePRODirectory();

  function updateAt(idx, next) {
    const copy = entries.slice();
    copy[idx] = next;
    onChange(copy);
  }
  function removeAt(idx) {
    onChange(entries.filter((_, i) => i !== idx));
  }
  function moveAt(idx, delta) {
    onChange(moveItem(entries, idx, delta));
  }
  function addNew() {
    onChange([
      ...entries,
      { id: newUuid(), ipi_cae_number: "", associated_pro: "", capacity: "", notes: "" },
    ]);
  }

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wide font-medium">
          IPI / CAE numbers
        </div>
        <span className="text-[11px] text-muted-foreground" data-testid={`${testid}-count`}>
          {entries.length}
        </span>
      </div>
      {entries.length === 0 && (
        <div className="mt-1 rounded border border-dashed text-[12px] text-muted-foreground px-3 py-2">
          No IPI / CAE identifiers for this party.
        </div>
      )}
      <div className="mt-1 space-y-1.5">
        {entries.map((e, idx) => (
          <IPIEntryRow
            key={e.id || `ipi-${idx}`}
            entry={e}
            proNames={proNames}
            onChange={(n) => updateAt(idx, n)}
            onRemove={() => removeAt(idx)}
            onMoveUp={() => moveAt(idx, -1)}
            onMoveDown={() => moveAt(idx, +1)}
            canMoveUp={idx > 0}
            canMoveDown={idx < entries.length - 1}
            testid={`${testid}-${idx}`}
          />
        ))}
      </div>
      <div className="mt-1.5">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addNew}
          data-testid={`${testid}-add`}
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" />
          Add IPI / CAE
        </Button>
      </div>
    </div>
  );
}

function IPIEntryRow({
  entry, proNames, onChange, onRemove, onMoveUp, onMoveDown, canMoveUp, canMoveDown, testid,
}) {
  const listId = `ipi-pros-${entry.id || "x"}`;
  function patch(fields) { onChange({ ...entry, ...fields }); }
  const valid = isLikelyValidIPI(entry.ipi_cae_number);

  return (
    <div
      className="rounded border bg-background px-2.5 py-2"
      data-testid={testid}
    >
      <div className="grid sm:grid-cols-12 gap-2 items-start">
        <div className="sm:col-span-4">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            IPI / CAE number
          </Label>
          <Input
            value={entry.ipi_cae_number || ""}
            onChange={(e) => patch({ ipi_cae_number: e.target.value })}
            placeholder="e.g. 123456789"
            className={`mt-0.5 h-8 font-mono text-[12.5px] ${
              !valid ? "border-amber-500 focus-visible:ring-amber-500" : ""
            }`}
            aria-invalid={!valid}
            data-testid={`${testid}-number`}
          />
          {!valid && (
            <div className="flex items-center gap-1 text-[10px] text-amber-700 mt-0.5" data-testid={`${testid}-warn`}>
              <AlertTriangle className="w-3 h-3" />
              Usually 9–11 digits. Check this identifier — will still save.
            </div>
          )}
        </div>
        <div className="sm:col-span-3">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Associated PRO
          </Label>
          <Input
            value={entry.associated_pro || ""}
            onChange={(e) => patch({ associated_pro: e.target.value })}
            list={listId}
            placeholder="ASCAP, BMI…"
            className="mt-0.5 h-8 text-[12.5px]"
            data-testid={`${testid}-pro`}
          />
          <datalist id={listId}>
            {(proNames || []).map((p) => (
              <option key={p.name} value={p.name} />
            ))}
          </datalist>
        </div>
        <div className="sm:col-span-4">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Capacity
          </Label>
          <Select
            value={entry.capacity || "unset"}
            onValueChange={(v) => patch({ capacity: v === "unset" ? "" : v })}
          >
            <SelectTrigger className="mt-0.5 h-8 text-[12.5px]" data-testid={`${testid}-capacity`}>
              <SelectValue placeholder="Optional" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="unset">—</SelectItem>
              {CAPACITY_OPTIONS.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-1 flex items-end justify-end gap-0.5 pb-0.5">
          <IconBtn title="Move up"   onClick={onMoveUp}   disabled={!canMoveUp}   testid={`${testid}-move-up`}><ArrowUp className="w-3 h-3" /></IconBtn>
          <IconBtn title="Move down" onClick={onMoveDown} disabled={!canMoveDown} testid={`${testid}-move-down`}><ArrowDown className="w-3 h-3" /></IconBtn>
          <IconBtn title="Remove IPI/CAE entry" onClick={onRemove} testid={`${testid}-remove`}>
            <X className="w-3 h-3" />
          </IconBtn>
        </div>
      </div>
      <div className="mt-1.5">
        <Input
          value={entry.notes || ""}
          onChange={(e) => patch({ notes: e.target.value })}
          placeholder="Notes (optional)"
          className="h-8 text-[12px]"
          data-testid={`${testid}-notes`}
        />
      </div>
    </div>
  );
}

// ---------- Small helpers --------------------------------------------------

function IconBtn({ children, title, onClick, disabled, testid }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className={`h-6 w-6 inline-flex items-center justify-center rounded border text-[hsl(347_60%_22%)]/70 ${
        disabled ? "opacity-30 cursor-not-allowed" : "hover:bg-secondary hover:text-[hsl(347_60%_22%)]"
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}
